import { Injectable } from '@angular/core';

import { userAgentUtil } from '../UserAgentUtil';

interface IPolyfill {
	element: string;
	enabled: string;
	exit: string;
	onchange: string;
	onerror: string;
	request: string;
}

const APIs = {
	w3: {
		element: 'fullscreenElement',
		enabled: 'fullscreenEnabled',
		exit: 'exitFullscreen',
		onchange: 'fullscreenchange',
		onerror: 'fullscreenerror',
		request: 'requestFullscreen'
	},
	newWebkit: {
		element: 'webkitFullscreenElement',
		enabled: 'webkitFullscreenEnabled',
		exit: 'webkitExitFullscreen',
		onchange: 'webkitfullscreenchange',
		onerror: 'webkitfullscreenerror',
		request: 'webkitRequestFullscreen'
	},
	oldWebkit: {
		element: 'webkitCurrentFullScreenElement',
		enabled: 'webkitIsFullScreen',
		exit: 'webkitCancelFullScreen',
		onchange: 'webkitfullscreenchange',
		onerror: 'webkitfullscreenerror',
		request: 'webkitRequestFullScreen'
	},
	moz: {
		element: 'mozFullScreenElement',
		enabled: 'mozFullScreen',
		exit: 'mozCancelFullScreen',
		onchange: 'mozfullscreenchange',
		onerror: 'mozfullscreenerror',
		request: 'mozRequestFullScreen'
	},
	ios: {
		element: 'webkitFullscreenElement',
		enabled: 'webkitFullscreenEnabled',
		exit: 'webkitExitFullscreen',
		onchange: 'webkitfullscreenchange',
		onerror: 'webkitfullscreenerror',
		request: 'webkitEnterFullscreen'
	},
	ms: {
		element: 'msFullscreenElement',
		enabled: 'msFullscreenEnabled',
		exit: 'msExitFullscreen',
		onchange: 'MSFullscreenChange',
		onerror: 'MSFullscreenError',
		request: 'msRequestFullscreen'
	}
};

/**
 * Native fullscreen polyfill service.
 *
 *    * vgFullscreen.onchange: String with the onchange event name.
 *    * vgFullscreen.onerror: String with the onerror event name.
 *    * vgFullscreen.isAvailable: Boolean with fullscreen availability.
 *    * vgFullscreen.isFullScreen: Boolean with current view mode.
 *    * vgFullscreen.exit: Exit fullscreen function.
 *    * vgFullscreen.request: Request for fullscreen access function.
 **/
@Injectable({
	providedIn: 'root'
}) // 'vgFullscreen'
export class VgFullscreenService {
	public isAvailable: boolean;

	private element: HTMLElement;
	private polyfill: IPolyfill;

	constructor() {
		this.init();
	}

	public get onchange(): string {
		return this.polyfill ?
			this.polyfill.onchange :
			null;
	}

	public get onerror(): string {
		return this.polyfill ?
			this.polyfill.onerror :
			null;
	}

	public isFullScreen(): boolean {
		if (!this.polyfill) {
			return false;
		}

		const isPolyfillElementInDocument: boolean = document[this.polyfill.element] != null;

		return this.element ?
			isPolyfillElementInDocument || (this.element as any).webkitDisplayingFullscreen :
			isPolyfillElementInDocument;
	}

	public request(elem: HTMLElement): void {
		if (this.polyfill) {
			this.element = elem;
			this.element[this.polyfill.request]();
		}
	}

	public exit(): void {
		if (this.polyfill) {
			document[this.polyfill.exit]();
		}
	}

	private init(): void {
		for (const browser in APIs) {
			if (APIs[browser].enabled in document) {
				this.polyfill = APIs[browser];
				break;
			}
		}

		// Override APIs on iOS
		if (userAgentUtil.isSafariIos) {
			this.polyfill = APIs.ios;
		}

		this.isAvailable = !!this.polyfill;
	}
}
